import React, { useEffect, useState, useRef, ReactNode } from 'react';
import ExcelJS, { Workbook, Worksheet } from 'exceljs';
import JSZip from 'jszip';
import * as FileSaver from 'file-saver';
import { v4 as uuidv4 } from 'uuid';
import { merge, pick } from 'lodash-es';
import { ratioValueFormat, thousandthFormat } from '@/pages/home/utils/common';
import ExcelConfig from '@/pages/intelligence/components/DownloadCommon/DownloadPage/configs/index';
import Downloading from '@/pages/intelligence/assets/imgs/downloading.png';
import XlsxIcon from '@/pages/opinion/assets/svg/xlsx-icon.svg';
import './index.scss';
import { Icon } from 'tea-component';
import { history, useIntl } from 'umi';
import { getBasePath } from '@/utils/utils';
import { urlToMap } from '@op/utils/url';
import http from '@/utils/http';
import {
  defaultCellStyle,
  styles,
  rowStyle,
  BOLD_FONT,
  PER_FMT_LIST,
} from '@intelligence/components/DownloadCommon/DownloadPage/common/baseConfig';
import formatters from '@intelligence/components/DownloadCommon/DownloadPage/common/formatters';
import {
  CurrefCurrent,
  DownloadParams,
  SheetData,
} from '@intelligence/components/DownloadCommon/DownloadPage/type/type';
import { analyseDataByDownloadKey } from '@intelligence/components/DownloadCommon/DownloadPage/common/dataSettingAtom';
import { addNumberColumn } from '@intelligence/components/DownloadCommon/DownloadPage/configs/steamNews';
import { appendMetricRatioKey } from '@intelligence/v3/utils/metric';
import { sortTime } from '@/pages/intelligence/utils/sort';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';
import {
  setAegisInfoAll,
  setFileName,
  getHasNextPageMarketProfile,
  sortDownloadGamesDataCommon,
  sortDownloadGamesMetrics,
} from '@/pages/intelligence/components/DownloadCommon/DownloadPage/utils';
import aegisLog from '@/log/aegis';
import { downloadConfig } from './common/conf/index';
import { getGameInfoById } from '@intelligence/services/common';
import { CUSTOM_FIELDS_RPD } from '@/pages/intelligence/pages/TopCharts/_config/enums';
// import { downloadApiListConst } from '@intelligence/components/DownloadCommon/DownloadPage/const/marketProfileStatsFixed';
import { fetchDownloadDataAutoPagination } from './utils/service';
import {
  groupByCrossAppMetric,
  groupByOverlapDetailsCrossAppMetric,
  checkAndReportEmptyData,
} from './utils';
import { isNotEmpty } from '@home/pages/studio/opinions/utils';
import { shouldUseSSE, fetchSSEDownload } from './utils/sse';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import { changeParamsMetricList } from '@intelligence/pages/MarketProfile/TabDetails/hooks/util';

const STORE_RANK_MORE_WEB_DOWNLOAD_KEYS = [
  'mobileStoreRankMore',
  'consoleStoreRankMore',
];
const STORE_RANK_TOP_WEB_DOWNLOAD_KEYS = [
  'mobileStoreRankTop',
  'consoleStoreRankTop',
];
const isStoreRankMoreWebDownloadKey = (key: string): boolean =>
  STORE_RANK_MORE_WEB_DOWNLOAD_KEYS.includes(key);
const isStoreRankTopWebDownloadKey = (key: string): boolean =>
  STORE_RANK_TOP_WEB_DOWNLOAD_KEYS.includes(key);
const isStoreRankWebDownloadKey = (key: string): boolean =>
  isStoreRankMoreWebDownloadKey(key) || isStoreRankTopWebDownloadKey(key);

const LIST_LIMIT = 5000;

const EXCEL_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=UTF-8';

const getListWhenUnknowData = (data?: any): any[] => {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data.Sheet1)) {
    return data.Sheet1;
  }

  return [];
};

/**
 * 处理下载操作。
 *
 * @param {Object} params - 下载参数对象。
 * @param {Record<string, any>} params.downLoadParams - 包含下载所需的参数。
 * @param {string} params.downloadKey - 用于获取配置的下载键。
 *
 * @returns {void} 无返回值。
 *
 * @description
 * 此函数根据提供的下载参数和下载键生成文件名，并将相关信息存储在 sessionStorage 中。
 * 然后，它会生成一个唯一的下载 ID，并使用该 ID 构建下载链接，最终在新窗口中打开该链接。
 *
 * @example
 * downloadAction({
 *   downLoadParams: { key1: 'value1', key2: 'value2' },
 *   downloadKey: 'exampleKey'
 * });
 */
export function downloadAction({
  downLoadParams,
  downloadKey,
  downloadQuery,
}: {
  downLoadParams: Record<string, any>;
  downloadKey: string;
  downloadQuery?: IRecord;
}) {
  // 通过downloadKey，拿到不同下载的下载参数、Sheet、表头等内容
  const configFunction = ExcelConfig[downloadKey];
  const commonSet = configFunction?.(downLoadParams);
  if (commonSet) {
    const { $$fileName } = commonSet;
    const newFileName = setFileName(downLoadParams, $$fileName);
    const storageParams = {
      params: downLoadParams,
      downloadKey,
      fileName: newFileName,
      commonSet,
    };
    const searchParams = new URLSearchParams(downloadQuery || {});
    const searchParamsString = searchParams.toString();
    const storageValue = JSON.stringify(storageParams);
    const downloadId = uuidv4();
    // 储存信息到sessionStorage,以供新下载弹窗使用参数等内容
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('downLoadParams')) {
          sessionStorage.removeItem(key);
        }
      }
      sessionStorage.setItem('downLoadParams', storageValue);
      sessionStorage.setItem(`downLoadParams-${downloadId}`, storageValue);
    } catch (e) {
      aegisLog.error({
        msg: 'download storage quota exceeded',
        downloadKey,
        error: e,
      });
      return;
    }
    setAegisInfoAll('download', storageParams, downloadId);
    let url = `${getBasePath()}/intelligence/downloadCommon?id=${downloadId}`;
    if (!!searchParamsString) {
      url = `${url}&${searchParamsString}`;
    }
    window.open(url, '_blank');
  }
}

const mergeParams = (params: any, fParams: any) => {
  return {
    ...params,
    ...pick(fParams, [
      'b_group_by_date',
      'b_group_by_region',
      'b_group_by_market',
      'b_group_by_platform',
      'b_group_by_segment',
      'b_growth_ratio',
    ]),
  };
};

/**
 * 下载页面组件
 *
 * 此组件负责处理文件下载的逻辑，包括：
 * - 初始化下载参数
 * - 根据下载参数决定下载文件的格式（CSV 或 XLSX）
 * - 处理下载过程中的状态管理（加载、成功、错误）
 * - 显示下载进度和状态信息
 *
 * @returns {JSX.Element} 下载页面的 JSX 元素
 *
 * @example
 * ```tsx
 * <DownloadPage />
 * ```
 *
 * @remarks
 * - 该组件使用了 `useIntl` 来处理国际化消息。
 * - 使用 `ExcelJS` 库生成 Excel 文件。
 * - 使用 `JSZip` 库将 CSV 文件打包成 ZIP 格式。
 * - 组件内部管理多个状态，包括当前文件名、下载状态和错误提示等。
 */
export default function DownloadPage() {
  const { isShowMaxDownloadRowsTips } = history?.location?.query || {};

  // 参考 https://databrain.intlgame.com/intelligence_detail/download.html?entityType=mobile&taskId=2825498722
  const { formatMessage } = useIntl();

  const [desc] = useState<ReactNode>(
    <>
      {formatMessage({
        id: 'Please do not close the tabs during the download process.',
      })}
    </>,
  );

  const [curFileName, setCurFileName] = useState<string>('filename');
  const [maxErrorVisible, setMaxErrorVisible] = useState<boolean>(false);

  const [limit, setLimit] = useState<number>(Infinity);
  const curRef = useRef<CurrefCurrent>({
    downloadFetchApi: '',
    fileName: '',
    params: undefined,
    hasNextPage: false, // 是否还有下一页
    downloadNum: 500, // 已下载数量
    downNumCur: 500, // 本次下载数量
  });

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading',
  );
  const map = urlToMap(window.location.href);

  useEffect(() => {
    download();
  }, []);

  /**
   * 下载文件的异步函数。
   *
   * 该函数首先从 sessionStorage 中获取下载参数，如果参数不存在，则直接返回。
   * 然后解析下载参数并设置相关状态信息。根据下载参数中的设置，决定使用哪种下载方法：
   * - 如果使用新方法，调用 `downloadByApi` 进行下载。
   * - 如果使用静态下载，调用 `dealStaticSheet` 处理下载。
   * - 否则，调用 `dealSheet` 进行默认下载。
   *
   * @async
   * @function download
   * @returns {Promise<void>} 返回一个 Promise，表示下载操作的完成状态。
   * @throws {Error} 如果下载过程中发生错误，将状态设置为 'error'。
   */
  const download = async () => {
    const mapId = map.get('id') || '';
    const downLoadParams = mapId
      ? sessionStorage.getItem(`downLoadParams-${mapId}`)
      : sessionStorage.getItem('downLoadParams');
    // 如果DownLoadParams不存在
    const isDownLoadParamsNoExist = ifDownLoadParamsExist(downLoadParams);

    // 如果不存在则直接返回
    if (isDownLoadParamsNoExist) return;
    try {
      // 解析下载参数
      const curDownLoadParams: DownloadParams = JSON.parse(
        typeof downLoadParams === 'string' ? downLoadParams : '{}',
      );

      setAegisInfoAll('downloadPageInit', curDownLoadParams, mapId);

      const { params, fileName, commonSet, downloadKey } = curDownLoadParams;
      const {
        downloadFetchApi = 'downloadKeyMetrics',
        limit: curLimit = Infinity,
        total: curTotal = -Infinity,
        sheetName: sheetNames = ['Sheet'],
        isUsingNewMethod,
        isUsingStaticDownload,
        downloadApiListObj,
        downloadMode,
        downloadConfigKey,
      } = commonSet;
      // console.debug('commonSet', commonSet);
      curRef.current = { ...commonSet, params, fileName, downloadKey };
      // console.debug('curRef.current', curRef.current);
      setCurFileName(fileName);
      curLimit && setLimit(curLimit);
      // 如果下载的文件数量超过最大的限制数量，则提示异常
      const isOverMax = isOverMaxLimit(curLimit, curTotal);
      // console.debug('isOverMax', isOverMax);
      if (isOverMax) return;
      if (!downloadFetchApi && isEmpty(downloadApiListObj)) return;
      if (isUsingNewMethod) {
        // 特殊下载，mobile counties页面的下载
        await downloadByApi();
      } else if (isUsingStaticDownload) {
        // 特殊下载，德康那边的
        await dealStaticSheet(sheetNames);
      } else if (downloadMode === 'commonConf') {
        await dealSheetPlus(downloadConfigKey);
      } else {
        // 默认下载
        await dealSheet(sheetNames);
      }
    } catch (e) {
      setStatus('error');
      console.log('error download', e);
      // 下载报错上报
      aegisLog.error({
        errorDownload: e,
      });
    } finally {
      if (mapId) {
        sessionStorage.removeItem(`downLoadParams-${mapId}`);
        sessionStorage.removeItem('downLoadParams');
      }
    }
  };

  // 单独处理marketProfile-stats-游戏
  const getGameAllDatas = async (
    downloadApiListObj: any,
    params: any,
    pageGame: any = 1,
  ) => {
    const isGrowthRatio = params.downlaod_date_method !== 'aggregated';
    const paramsValueTemp = downloadApiListObj?.byGames?.apiParams || {};
    const paramsValue = {
      ...paramsValueTemp,
      page: pageGame,
    };
    try {
      // 如果是bydate
      if (isGrowthRatio) {
        const gameDataRes = await getDownloadData(
          paramsValue,
          downloadApiListObj?.byGames?.api || '',
        );
        const gameListTemp = gameDataRes?.ids || [];
        const totalValue = gameDataRes?.total || 0;
        const gameIds = gameListTemp?.map((item: any) => item.unified_id);

        const paramsGamesTrend = {
          ...downloadApiListObj?.byGames?.apiParams,
          select_code: gameIds,
        };
        const gameLineDatas = await getDownloadData(
          paramsGamesTrend,
          '/api/v1/intelligence_pc/mobileGamesTrend',
        );
        const gameTrends = gameLineDatas?.list || [];

        const metricListValue = gameListTemp?.map((itemPa: any) => {
          const gameCurObj = gameTrends?.find(
            (itemTrend: any) => itemTrend.id === itemPa.unified_id,
          );
          return {
            ...itemPa,
            metric_value_list: gameCurObj?.metric_value_trends || [],
          };
        });

        return {
          list: metricListValue,
          total: totalValue,
        };
      }
      // 如果不是bydate
      const paramsGamesTrend = {
        ...downloadApiListObj?.byGames?.apiParams,
        data_tag: 'games',
        page: pageGame,
      };
      const gameDatas = await getDownloadData(
        paramsGamesTrend,
        '/api/v1/intelligence_pc/mobileGames',
      );
      // const gameDatasList = gameDatas?.list || [];
      // console.log('gameDatasList', gameDatasList);
      return gameDatas;
    } catch (error) {
      // 统一捕获错误（A或B失败都会进入此处）
      console.error('请求失败:', error);
      return {};
    }
  };

  // 单独处理marketProfile-stats-company
  const getCompanyAllDatas = async (
    downloadApiListObj: any,
    params: any,
    pageCompany: any = 1,
  ) => {
    const isGrowthRatio = params.downlaod_date_method !== 'aggregated';

    const paramsValueTemp = downloadApiListObj?.byGames?.apiParams || {};
    const paramsValue = {
      ...paramsValueTemp,
      page: pageCompany,
    };

    try {
      // 如果是bydate
      if (isGrowthRatio) {
        const companyDataRes = await getDownloadData(
          paramsValue,
          downloadApiListObj?.byCompanies?.api || '',
        );
        const companyListTemp = companyDataRes?.IDs || [];
        const totalValue = companyDataRes?.total || 0;
        const companyIds = companyListTemp?.map((item: any) => item.uuid) || [];

        let companyLineDatasTemp = [];
        if (Array.isArray(companyIds) && companyIds?.length > 0) {
          const paramsCompanyTrend = {
            ...downloadApiListObj?.byCompanies?.apiParams,
            select_code: companyIds,
          };
          delete paramsCompanyTrend.page;
          delete paramsCompanyTrend.page_size;

          const companyLineDatas = await getDownloadData(
            paramsCompanyTrend,
            '/api/v1/intelligence_pc/mobileCompanyTrend',
          );
          companyLineDatasTemp = companyLineDatas?.list || [];
        }
        const metricListValue = companyListTemp?.map((itemPa: any) => {
          const companyCurObj = companyLineDatasTemp?.find(
            (itemTrend: any) => itemTrend.uuid === itemPa.uuid,
          );
          return {
            ...itemPa,
            metric_value_list: companyCurObj?.metric_value_trends || [],
          };
        });

        return {
          list: metricListValue,
          total: totalValue,
        };
      }
      // 如果不是bydate
      const paramsCompanyTrend = {
        ...downloadApiListObj?.byCompanies?.apiParams,
        page: pageCompany,
      };
      const companyDatas = await getDownloadData(
        paramsCompanyTrend,
        '/api/v1/intelligence_pc/mobileCompany',
      );
      // const companyDatasList = companyDatas?.list || [];
      // console.log('companyDatasListcompanyDatasList', companyDatasList);
      return companyDatas;
    } catch (error) {
      // 统一捕获错误（A或B失败都会进入此处）
      console.error('请求失败:', error);
      return {};
    }
  };

  /**
   * 处理下载表格数据并生成 Excel 文件。
   *
   * @param sheetNames - 要下载的工作表名称数组。
   *
   * @returns {Promise<void>} - 返回一个 Promise，表示下载过程完成。
   *
   * @remarks
   * 该函数会根据提供的参数进行分页下载，并将数据填充到 Excel 工作表中。
   * - 支持处理特殊场景的数据格式。
   * - 可选地添加基本信息到每个数据项中。
   * - 处理完成后，会将生成的工作簿写入文件。
   */
  const dealSheet = async (sheetNames: string[]) => {
    let hasNextPage = true;
    let hasGameNextPage = true;
    let hasCompanyNextPage = true;
    // @ts-ignore
    const {
      params,
      downloadFetchApi,
      dataKey,
      hasNumberColumn,
      fileName,
      fParams = {},
      downloadApiParams = {},
      downloadKey,
      needBasicInfo,
      downloadApiListObj = {},
      isNoHeadTitle = false,
    } = curRef.current;
    // default: page: 1, page_size: 10000
    // 需要分页下载
    params.page = 1;
    params.page_size = params.page_size || LIST_LIMIT;
    // @ts-ignore
    // console.debug('curRef.current', curRef.current);
    // console.debug('sheetNamessheetNames', sheetNames);
    const workbook = new ExcelJS.Workbook();
    const sheetList = sheetNames?.map((sheetName) => {
      const sheet = workbook.addWorksheet(sheetName);
      const columns = curRef.current[sheetName];
      // 是否需要设置默认表头
      setDownloadHeaderByTitle(sheet, columns, isNoHeadTitle);
      return { sheet, columns, sheetName };
    });
    const mobileStoreRankMoreSheetDataMap: Record<string, any[]> = {};
    let mobileStoreRankAllData: any = {};

    const collectMobileStoreRankMoreSheetData = (
      sheetName: string,
      data: Record<string, any>[] = [],
      pageRes?: any,
    ) => {
      if (!isStoreRankWebDownloadKey(downloadKey)) {
        return;
      }
      mobileStoreRankMoreSheetDataMap[sheetName] = [
        ...(mobileStoreRankMoreSheetDataMap[sheetName] || []),
        ...(Array.isArray(data) ? data : []),
      ];
      if (pageRes?.ext) {
        mobileStoreRankAllData = {
          ...mobileStoreRankAllData,
          ext: merge(mobileStoreRankAllData?.ext || {}, pageRes.ext),
        };
      }
    };

    const flushMobileStoreRankMoreSheetData = () => {
      if (!isStoreRankWebDownloadKey(downloadKey)) {
        return;
      }
      const paramsNew = mergeParams(params, fParams);
      sheetList?.forEach(({ sheet, sheetName }) => {
        setDownloadDataByPagingData(
          mobileStoreRankMoreSheetDataMap[sheetName] || [],
          sheet,
          curRef.current[sheetName],
          downloadKey,
          mobileStoreRankAllData,
          paramsNew,
        );
        defaultRowNumFmt(sheet);
      });
    };

    let fetchParams = params?.fp
      ? { ...params?.fp, ...fParams }
      : { ...params, ...fParams };
    // 为确保下载参数干净，定义纯净下载接口对应参数 downloadApiParams
    if (!isEmpty(downloadApiParams)) {
      fetchParams = downloadApiParams;
    }
    if (isEmpty(downloadApiParams) && isNotEmpty(params?.source_metrics)) {
      fetchParams = {
        ...fetchParams,
        source_metrics: merge(
          fetchParams?.source_metrics || {},
          params.source_metrics,
        ),
      };
    }
    // 只为了mobileStoreRankMore 这个特殊场景添加的
    let fetchReqFirstTime = true;
    let totalValue = 0;
    let totalGameValue = 0;
    let totalCompanyValue = 0;
    // 已下载数量
    let downloadNum = 500;
    let downloadMarketGameNum = 500;
    let downloadMarketCompanyNum = 500;
    // 本次下载数量
    let downNumCur = 500;
    let downGameNumCur = params.select_top < 500 ? params.select_top : 500;
    let downCompanyNumCur = params.select_top < 500 ? params.select_top : 500;
    // 查询时间线数据时，最大下载数量
    const maxDownloadNum = 100000;

    let pageGame = 1;
    let pageCompany = 1;

    // 如果是多页签混合下载
    if (!isEmpty(downloadApiListObj)) {
      do {
        let res: any = null;
        // debugger;
        if (downloadKey === 'marketProfileStats') {
          const isOperationData = dataKey?.includes('Operation Data');
          const isByGames = dataKey?.includes('Game Data');
          const isByMainGenre = dataKey?.includes('Main Genre Data');
          const isByCompanies = dataKey?.includes('Companies');

          // console.log('pageGame!!!!!!!!!!!!!!!!!!', pageGame);
          // console.log('pageCompany!!!!!!!!!!!!!!!!!!', pageCompany);

          const fetchList = [
            isOperationData && downloadMarketGameNum === 500
              ? getDownloadData(
                downloadApiListObj?.operationData?.apiParams || {},
                downloadApiListObj?.operationData?.api || '',
              )
              : '',
            isByGames && hasGameNextPage
              ? getGameAllDatas(downloadApiListObj, params, pageGame)
              : '',
            isByMainGenre && downloadMarketGameNum === 500
              ? getDownloadData(
                downloadApiListObj?.byMainGenre?.apiParams || {},
                downloadApiListObj?.byMainGenre?.api || '',
              )
              : '',
            isByCompanies && hasCompanyNextPage
              ? getCompanyAllDatas(downloadApiListObj, params, pageCompany)
              : '',
          ];
          try {
            const [
              operationDataRes = {},
              byGamesRes = {},
              byMainGenreRes = {},
              byCompaniesRes = {},
            ] = await Promise.all(fetchList);
            // console.log('operationDataRes', operationDataRes);
            // console.log('byGamesRes', byGamesRes);
            totalGameValue = Number(byGamesRes?.total);
            // console.log('byMainGenreRes', byMainGenreRes);
            // console.log('byCompaniesRes', byCompaniesRes);
            totalCompanyValue = Number(byCompaniesRes?.total);

            let gameResList = byGamesRes.list || [];
            let companyResList = byCompaniesRes.list || [];
            if (Array.isArray(gameResList) && gameResList?.length > 0) {
              gameResList = gameResList?.slice(0, downGameNumCur);
            }
            if (Array.isArray(companyResList) && companyResList?.length > 0) {
              companyResList = companyResList?.slice(0, downCompanyNumCur);
            }

            res = {
              'Operation Data': operationDataRes,
              'Game Data': gameResList,
              'Main Genre Data': byMainGenreRes,
              Companies: companyResList,
            };
          } catch (error) {
            console.error(error);
          } finally {
          }
        } else if (downloadKey === 'genreProfileDetailStats') {
          const isOperationData = dataKey?.includes('Operation Data');
          const isByGames = dataKey?.includes('Game Data');
          const byCountries = dataKey?.includes('Country');
          const isByCompanies = dataKey?.includes('Company');

          const fetchList = [
            isOperationData
              ? getDownloadData(
                {
                  ...downloadApiListObj?.operationData?.apiParams,
                  start_date:
                    params.operationDataStart ||
                    downloadApiListObj?.operationData?.apiParams?.start_date,
                  end_date:
                    params.operationDataEnd ||
                    downloadApiListObj?.operationData?.apiParams?.end_date,
                },
                downloadApiListObj?.operationData?.api || '',
              )
              : '',
            isByGames
              ? getDownloadData(
                {
                  ...downloadApiListObj?.byGames?.apiParams,
                  metric_condition: { game_ids: params.gamesIdList },
                  page_size: maxDownloadNum,
                },
                downloadApiListObj?.byGames?.api || '',
              )
              : '',
            byCountries
              ? getDownloadData(
                {
                  ...downloadApiListObj?.byCountries?.apiParams,
                  regions:
                    params.countriesList ||
                    downloadApiListObj?.byCountries?.apiParams?.regions,
                  page_size: maxDownloadNum,
                },
                downloadApiListObj?.byCountries?.api || '',
              )
              : '',
            isByCompanies
              ? getDownloadData(
                {
                  ...downloadApiListObj?.byCompanies?.apiParams,
                  metric_condition: { publisher_ids: params.companiesList },
                  page_size: maxDownloadNum,
                },
                downloadApiListObj?.byCompanies?.api || '',
              )
              : '',
          ];
          try {
            const [
              operationDataRes = {},
              byGamesRes = {},
              byCountriesRes = {},
              byCompaniesRes = {},
            ] = await Promise.all(fetchList);

            res = {
              'Operation Data': operationDataRes,
              'Game Data': byGamesRes,
              Country: byCountriesRes,
              Company: byCompaniesRes,
            };
          } catch (error) {
            console.error(error);
          } finally {
          }
        } else if (downloadKey === 'genreProfileTopCharts') {
          const isMainGenre = dataKey?.includes('Main Genre');
          const isSubGenre = dataKey?.includes('Sub Genre');
          const isMainSubGenre = dataKey?.includes('Main+Sub Genre');

          const fetchList = [
            isMainGenre
              ? getDownloadData(
                downloadApiListObj?.main?.apiParams || {},
                downloadApiListObj?.main?.api || '',
              )
              : '',
            isSubGenre
              ? getDownloadData(
                downloadApiListObj?.sub?.apiParams || {},
                downloadApiListObj?.sub?.api || '',
              )
              : '',
            isMainSubGenre
              ? getDownloadData(
                downloadApiListObj?.mainsub?.apiParams || {},
                downloadApiListObj?.mainsub?.api || '',
              )
              : '',
          ];
          try {
            const [resMainGenre = {}, resSubGenre = {}, resMainSubGenre = {}] =
              await Promise.all(fetchList);

            res = {
              'Main Genre': resMainGenre,
              'Sub Genre': resSubGenre,
              'Main+Sub Genre': resMainSubGenre,
            };
          } catch (error) {
            console.error(error);
          } finally {
          }
        }

        // console.debug('resres22111111', res);

        // debugger;
        let list: any = {};
        let allData: any = res || {};
        const { ext_fields = {} } = allData || {};

        const { market_count = [] } = ext_fields;
        let marketList = [...market_count];
        if (Array.isArray(marketList) && marketList?.length > 0) {
          marketList = marketList?.sort((a, b) => {
            const aDate = a?.date;
            const bDate = b?.date;
            return sortTime(aDate, bDate);
          });
        }

        allData = {
          ...allData,
          ext_fields: {
            ...allData?.ext_fields,
            market_count: marketList,
          },
        };

        // console.log('ddddd', list);
        // console.log('sheetNames', sheetNames);
        // console.log('dataKey', dataKey);
        // debugger;
        sheetNames?.forEach((sheetNameItem, idx) => {
          if (dataKey) {
            const keys = dataKey[idx];
            list[sheetNameItem] = (keys ? res?.[keys] : res) || [];
          } else {
            list[sheetNameItem] = res || [];
          }
          // const keys = dataKey[idx];
          // console.log('keys', keys);
          // list[sheetNameItem] = keys ? res?.[keys] : res || [];
        });
        // console.log('hasNumberColumn', hasNumberColumn);
        // 数据处理
        list = hasNumberColumn
          ? addNumberColumn({
            list: getListWhenUnknowData(list),
            page: fetchParams.page,
            page_size: fetchParams.page_size,
          })
          : list;
        if (needBasicInfo) {
          Object.keys(list)?.forEach((ele: any) => {
            list[ele] = list[ele]?.map((item: any) => {
              item.basic_info = res?.ext_fields?.basic_info;
              return item;
            });
          });
        }
        // console.debug('sheetListsheetList222', sheetList);
        // console.debug('listlistlist222', list);
        // eslint-disable-next-line no-loop-func
        sheetList?.forEach(({ sheet, columns, sheetName }) => {
          let columnsValue = columns;
          if (isStoreRankWebDownloadKey(downloadKey)) {
            if (fetchReqFirstTime) {
              fetchReqFirstTime = false;
              if (isStoreRankMoreWebDownloadKey(downloadKey)) {
                setSheetHeadByReqMerticList(sheet, res);
              }
            }
            collectMobileStoreRankMoreSheetData(sheetName, list[sheetName], res);
            return;
          }
          // console.debug('columnsValue', columnsValue);
          // console.debug('sheet', sheet);
          // console.debug('shsheetNameeet', sheetName);
          // debugger;

          // 通过excel配置与下载参数，完成数据整合
          const paramsNew = mergeParams(params, fParams);
          setDownloadDataByPagingData(
            list[sheetName],
            sheet,
            columnsValue,
            downloadKey,
            allData,
            paramsNew,
          );
          defaultRowNumFmt(sheet);
        });
        // console.log('hasNextPage-list', list);
        // console.log('hasNextPage-downloadKey', downloadKey);
        // 特殊处理pcConsoleTopCharts榜单下载，循环调用多数据下载
        if (downloadKey === 'marketProfileStats') {
          hasNextPage = false;
          const numGameObj: any =
            getHasNextPageMarketProfile(
              totalGameValue,
              pageGame,
              params,
              downloadMarketGameNum,
            ) || {};
          const numCompanyObj: any =
            getHasNextPageMarketProfile(
              totalCompanyValue,
              pageCompany,
              params,
              downloadMarketCompanyNum,
            ) || {};
          // console.log('hasNextPage222numObjnumGameObj', numGameObj);
          // console.log('hasNextPage222numObjnumCompanyObj', numCompanyObj);
          // console.log('downloadMarketGameNumdownloadMarketGameNum', downloadMarketGameNum);
          // console.log('downGameNumCurdownGameNumCur', downGameNumCur);
          // console.log('downCompanyNumCur', downCompanyNumCur);
          hasGameNextPage = numGameObj?.hasNextPage;
          downloadMarketGameNum = numGameObj?.reMaNumCur + downloadMarketGameNum;
          downGameNumCur = numGameObj?.reMaNumCur;
          hasCompanyNextPage = numCompanyObj?.hasNextPage;
          downloadMarketCompanyNum =
            numCompanyObj?.reMaNumCur + downloadMarketCompanyNum;
          downCompanyNumCur = numCompanyObj?.reMaNumCur;
        } else {
          hasNextPage = setHasNextPage(res, list, fetchParams);
          // console.log('hasNextPage', hasNextPage);
        }
        pageGame += 1;
        pageCompany += 1;
        // console.log('hasNextPage~~~', hasNextPage);
        // console.log('hasGameNextPage~~~', hasGameNextPage);
        // console.log('hasCompanyNextPage~~~~~', hasCompanyNextPage);
      } while (
        hasNextPage ||
        (downloadKey === 'marketProfileStats' &&
          (hasGameNextPage || hasCompanyNextPage))
      );
      flushMobileStoreRankMoreSheetData();
      setSummarySheet(workbook);
      // 空数据检测
      checkAndReportEmptyData(workbook, {
        downloadKey,
        downloadId: map.get('id') || '',
        params,
        fileName: curRef.current.fileName,
      });
      await writeFile(workbook, fileName);
    } else {
      do {
        let res: any = null;
        // debugger;
        if (downloadKey === 'pcConsoleTopCharts' || downloadKey === 'databrainCalibrationPCConsoleTopCharts') {
          const resTemp = await getDownloadData(fetchParams, downloadFetchApi);
          const { list = [], total = '' } = resTemp || {};
          let listNew = list;

          if (!!fParams?.b_group_by_device) {
            listNew = list?.map((item: any) => {
              const metrics = item?.metric_value_list?.filter((subItem: any) => {
                return subItem?.platform?.toLowerCase() !== 'aggregated';
              });
              return { ...item, metric_value_list: metrics || [] };
            });
          }

          if (Array.isArray(listNew) && listNew?.length > 0) {
            res = listNew?.slice(0, downNumCur);
          } else {
            res = listNew;
          }
          totalValue = Number(total);
        } else {
          // 如果是MarketProfile榜单下载，国家排序和数量根据榜单宽表来定
          if (downloadKey === 'mobileMarketProfileTopCharts') {
            if (params.select_top) {
              // select_top选取前多少个
              const resListTemp =
                params.tableData?.length > 0
                  ? params.tableData?.slice(0, params.select_top)
                  : [];
              const resList = resListTemp?.map((item: any) => item.market);
              fetchParams = {
                ...fetchParams,
                regions: resList,
              };
            } else if (
              params.marketsSelected &&
              Array.isArray(params.marketsSelected) &&
              params.marketsSelected?.length > 0
            ) {
              // 按宽表勾选过滤，
              fetchParams = {
                ...fetchParams,
                regions: params.marketsSelected,
              };
            }
          }
          // console.debug('resres222333fetchParams3fetchParams', fetchParams);
          if (shouldUseSSE(fetchParams)) {
            res = await fetchSSEDownload({
              url: downloadFetchApi,
              params: fetchParams,
              onData: () => {},
              onProgress: () => {},
              onError: (error) => {
                console.error('SSE download error:', error);
              },
            });
            hasNextPage = false;
          } else {
            res = await getDownloadData(fetchParams, downloadFetchApi);
          }
        }

        // console.debug('resres2223333', res);
        // debugger;

        let list: any = {};
        let allData: any = res;
        const { ext_fields = {} } = allData || {};

        const { market_count = [] } = ext_fields;
        let marketList = [...market_count];
        if (Array.isArray(marketList) && marketList?.length > 0) {
          marketList = marketList?.sort((a, b) => {
            const aDate = a?.date;
            const bDate = b?.date;
            return sortTime(aDate, bDate);
          });
        }

        allData = {
          ...allData,
          ext_fields: {
            ...allData.ext_fields,
            market_count: marketList,
          },
        };

        // console.debug('ddddd', list);
        // console.debug('sheetNames', sheetNames);
        // debugger;
        sheetNames?.forEach((sheetNameItem, idx) => {
          if (dataKey) {
            const keys = dataKey[idx];
            list[sheetNameItem] = keys ? res?.[keys] : res || [];
          } else {
            list[sheetNameItem] = res || [];
          }
        });
        // 数据处理
        list = hasNumberColumn
          ? addNumberColumn({
            list: getListWhenUnknowData(list),
            page: fetchParams.page,
            page_size: fetchParams.page_size,
          })
          : list;
        if (needBasicInfo) {
          Object.keys(list)?.forEach((ele: any) => {
            list[ele] = list[ele]?.map((item: any) => {
              item.basic_info = res?.ext_fields?.basic_info;
              return item;
            });
          });
        }
        // console.debug('sheetListsheetList', sheetList);
        // eslint-disable-next-line no-loop-func
        sheetList?.forEach(({ sheet, columns, sheetName }) => {
          let columnsValue = columns;
          if (isStoreRankWebDownloadKey(downloadKey)) {
            if (fetchReqFirstTime) {
              fetchReqFirstTime = false;
              if (isStoreRankMoreWebDownloadKey(downloadKey)) {
                setSheetHeadByReqMerticList(sheet, res);
              }
            }
            collectMobileStoreRankMoreSheetData(sheetName, list?.[sheetName], res);
            return;
          }
          // console.debug('columnsValue', columnsValue);
          // console.debug('sheet', sheet);
          // debugger;

          // 通过excel配置与下载参数，完成数据整合
          const paramsNew = mergeParams(params, fParams);
          setDownloadDataByPagingData(
            list?.[sheetName],
            sheet,
            columnsValue,
            downloadKey,
            allData,
            paramsNew,
          );
          defaultRowNumFmt(sheet);
        });
        // console.debug('hasNextPage-list22222', list);
        // console.debug('hasNextPage-downloadKey', downloadKey);
        // 特殊处理pcConsoleTopCharts榜单下载，循环调用多数据下载
        if (
          downloadKey === 'pcConsoleTopCharts' ||
          downloadKey === 'databrainCalibrationPCConsoleTopCharts'
        ) {
          const numObj: any =
            getHasNextPage(totalValue, fetchParams, params, downloadNum) || {};
          hasNextPage = numObj?.hasNextPage;
          downloadNum = numObj?.reMaNumCur + downloadNum;
          downNumCur = numObj?.reMaNumCur;
          // fetchParams.page_size = numObj?.pageSizeCur;
          // console.log('hasNextPage222numObj', numObj);
          // console.log('hasNextPage222numObjfetchParams', fetchParams);
        } else {
          hasNextPage = setHasNextPage(res, list, fetchParams);
          // console.debug('hasNextPage', hasNextPage);
        }
        fetchParams.page += 1;
      } while (hasNextPage);
      flushMobileStoreRankMoreSheetData();
      setSummarySheet(workbook);
      // 空数据检测
      checkAndReportEmptyData(workbook, {
        downloadKey,
        downloadId: map.get('id') || '',
        params,
        fileName: curRef.current.fileName,
      });
      await writeFile(workbook, fileName);
    }
  };

  const dealSheetPlus = async (downloadConfigKey: string) => {
    // @ts-ignore
    const {
      params,
      downloadFetchApi,
      fileName,
      downloadApiParams,
      downloadKey,
    } = curRef.current || {};
    // default: page: 1, page_size: 10000
    // 是否还有下一页
    // eslint-disable-next-line react-hooks/rules-of-hooks
    // const hasNextPageObj: any = useRef({
    //   hasNextPage: false, // 是否还有下一页
    //   downloadNum: 500, // 已下载数量
    //   downNumCur: 500, // 本次下载数量
    // });
    if (params) {
      // 需要分页下载
      params.page = 1;
      params.page_size = params.page_size || LIST_LIMIT;
    }
    // @ts-ignore
    const workbook = new ExcelJS.Workbook();
    const fetchParams: any = downloadApiParams || {};

    /*  是否停止继续下载，分两种条件
     * 1、下载个数超过用户选择个数（通常为游戏个数）
     * 2、下载数量超过10万条（通常为By Time时，选择时间范围和条件太宽，导致数据量大）
     */
    let hasNextPageCurRef = false; // 是否还有下一页
    let downloadNumCurRef = 0; // 已下载数量
    let downNumCurRef = 500; // 本次下载数量

    // 下载结果
    let res: any = [];
    // 获取 downloadApiListObj
    const downloadApiListObj = curRef.current?.downloadApiListObj;

    if (downloadKey === 'casualCompetitorsTopPublishers') {
      // CasualCompetitors 头部厂商下载：Companies 和 Games 两个接口
      try {
        const fetchDataWithSSE = async (apiParams: any, api: string, apiName: string) => {
          if (shouldUseSSE(apiParams)) {
            return fetchSSEDownload({
              url: api,
              params: apiParams,
              onData: (_data) => { },
              onProgress: (_progress, _current, _total) => { },
              onError: (error) => {
                console.error(`${apiName} SSE download error:`, error);
              },
            });
          }

          return getDownloadData(apiParams, api);
        };

        const companiesApiParams = downloadApiListObj?.companies?.apiParams || {};
        const companiesApi = downloadApiListObj?.companies?.api || '';
        const gamesApiParams = downloadApiListObj?.games?.apiParams || {};
        const gamesApi = downloadApiListObj?.games?.api || '';

        const companiesRes = await fetchDataWithSSE(companiesApiParams, companiesApi, 'Companies');
        const gamesRes = await fetchDataWithSSE(gamesApiParams, gamesApi, 'Games');

        res = {
          Companies: companiesRes || {},
          Games: gamesRes || {},
        };
      } catch (error) {
        console.error('casualCompetitorsTopPublishers download error:', error);
        res = { Companies: {}, Games: {} };
      }
    } else if (downloadKey === 'mobileTopChartsRPD') {
      const isGroupBy = !!fetchParams?.group_by?.length;
      const granularity = fetchParams?.granularity || 'daily';

      const getDownloadDataIntelligence = async (
        params: any,
        downloadFetchApi: string,
      ) => {
        let orderGameIdList: string[] = params?.game_ids || [];
        let list: any[] = [];

        const paramsCurRef = curRef.current?.params;
        const orderBys = fetchParams?.order_by || [];
        const metricOrderBys = orderBys?.slice(orderBys?.length - 1);
        // 如果是data_method:"checkedLines",则不需要调获取游戏idlist的接口
        let idListRes = [];
        if (paramsCurRef?.data_method === 'checkedLines') {
          orderGameIdList = paramsCurRef?.select_ids;
        } else {
          idListRes = await getDownloadData(
            {
              ...params,
              group_by: [],
              order_by: metricOrderBys,
            },
            downloadFetchApi,
          );
        }
        if (paramsCurRef?.data_method !== 'checkedLines') {
          const orderGameIdListNew: string[] = idListRes?.list?.map(
            (item: any) => item?.unified_id,
          );
          if (!orderGameIdList?.length) {
            orderGameIdList = orderGameIdListNew;
          }
        }

        if (isGroupBy) {
          const getDownloadDataAutoPagination = fetchDownloadDataAutoPagination(
            getDownloadData,
            {
              pageField: 'page',
              pageSizeField: 'page_size',
              listField: 'list',
              totalField: 'total',
              onPageFetched: (res) => {
              },
            },
          );

          for (let i = 0; i < orderGameIdList?.length; i += 100) {
            const currentGameIds = orderGameIdList?.slice(i, i + 100) || [];
            const itemRes = await getDownloadDataAutoPagination(
              {
                ...params,
                unified_ids: currentGameIds || [],
                page_size: 5000,
              },
              downloadFetchApi,
            );
            list = [...list, ...(itemRes?.list || [])];
          }
        } else if (paramsCurRef?.data_method === 'selectAll') {
          list = idListRes?.list || [];
        } else {
          for (let i = 0; i < orderGameIdList?.length; i += 5000) {
            const currentGameIds = orderGameIdList?.slice(i, i + 5000) || [];
            const itemRes = await getDownloadData(
              {
                ...params,
                unified_ids: currentGameIds || [],
                page_size: currentGameIds?.length,
              },
              downloadFetchApi,
            );
            list = [...list, ...(itemRes?.list || [])];
          }
        }

        // 根据 orderGameIdList 查询所有游戏详情，并把详情合并到 list 中 unified_id 相同的一项
        if (Array.isArray(orderGameIdList) && orderGameIdList.length > 0) {
          try {
            const gameInfoParams = {
              entity_type: fetchParams?.entity_type,
              ids: orderGameIdList,
              custom_fields: CUSTOM_FIELDS_RPD,
              level: 'custom',
            } as any;
            const gameInfoRes = await getGameInfoById(gameInfoParams);
            const gameInfoMap = gameInfoRes?.data || {};
            list = list.map((item: any) => ({
              ...item,
              ...gameInfoMap?.[item?.unified_id],
            }));
          } catch (error) {
          }
        }
        return [...list];
      };

      const fetchParamsNew = { ...fetchParams, granularity };

      res = await getDownloadDataIntelligence(fetchParamsNew, downloadFetchApi);

    } else if (downloadKey === 'trueAllDataDownloadExcel') {
      do {
        await getDownloadData(fetchParams, downloadFetchApi)
          // eslint-disable-next-line no-loop-func
          .then(async (result) => {
            // console.log('resultresult222', result);
            // 经过过滤与去重的idsList结果
            const listTemp = result?.list || [];
            const total = result?.total || listTemp?.length; // 搜索不返回total用表格长度计算
            // 暂时单独处理
            if (result?.list && result?.list.length > 0) {
              const gameInfoParams = {
                entity_type: 'auto',
                ids: result?.list?.map((item: any) => item?.game_id),
                custom_fields: [
                  'cover',
                  'entity_name',
                  'other_name',
                  'publisher',
                  'country_abbre',
                  'country_name_en',
                  'parent_publisher',
                  'developer_country_name_en',
                  'developer_country_abbre',
                  'developer_parent',
                  'release_time',
                ],
                level: 'custom',
              };

              try {
                const gameInfoRes = await getGameInfoById(gameInfoParams);
                // translateStateResponse 公共方法，二合一转为旧数据格式
                const gameInfoMap = gameInfoRes?.data || {};

                // 实际要拿的数据
                // let listValue: any = [];
                // 转换数据结构
                const list =
                  listTemp?.map((item: any) => {
                    return {
                      ...item,
                      ...gameInfoMap?.[item?.game_id],
                    };
                  }) || [];
                res = [...res, ...list];

                const numObj: any =
                  getTrueHasNextPage(
                    total,
                    fetchParams,
                    params,
                    downloadNumCurRef,
                  ) || {};
                hasNextPageCurRef = numObj?.hasNextPage;
                downloadNumCurRef = numObj?.reMaNumCur + downloadNumCurRef;
                downNumCurRef = numObj?.reMaNumCur;
              } catch (error) {
                res = [];
              }
            } else {
              res = [];
            }
          })
          // eslint-disable-next-line no-loop-func
          .catch(() => {
            res = [];
          });
        fetchParams.page += 1;

        // console.log('resresres ', res);
      } while (hasNextPageCurRef);
    } else if (
      [
        'mobileTopChartsMultiDataSource',
        'mobileOverlapTopCharts',
        'mobileOverlapDetails',
      ].includes(downloadKey)
    ) {
      const isGroupBy = !!fetchParams?.group_by?.length;
      const granularity = fetchParams?.granularity || 'daily';
      const granularittiesDefault = [granularity];
      const dateMethod =
        curRef.current?.params?.downlaod_date_method || 'aggregated';
      const dateTypes = curRef.current?.params?.download_date_type || [];
      const granularitties = (() => {
        if (dateMethod === 'aggregated') return granularittiesDefault;
        if (!Array.isArray(dateTypes) || dateTypes.length === 0)
          return granularittiesDefault;
        return dateTypes;
      })();

      const getDownloadDataIntelligence = async (
        params: any,
        downloadFetchApi: string,
      ) => {
        let orderGameIdList: string[] = params?.game_ids || [];
        let res: any = null;
        let list: any[] = [];
        let extEntityInfo: IRecord = {};

        const orderBys = fetchParams?.order_by || [];
        const metricOrderBys = orderBys?.slice(orderBys?.length - 1);
        const idListRes = await getDownloadData(
          {
            ...params,
            group_by: [],
            order_by: metricOrderBys,
          },
          downloadFetchApi,
        );
        const orderGameIdListNew: string[] = idListRes?.list?.map(
          (item: any) => item?.game_id,
        );
        if (!orderGameIdList?.length) {
          orderGameIdList = orderGameIdListNew;
        }

        if (isGroupBy) {
          const getDownloadDataAutoPagination = fetchDownloadDataAutoPagination(
            getDownloadData,
            {
              pageField: 'page',
              pageSizeField: 'page_size',
              listField: 'list',
              totalField: 'total',
              onPageFetched: (res) => {
                extEntityInfo = { ...extEntityInfo, ...res?.$ext?.entity_info };
              },
            },
          );

          for (let i = 0; i < orderGameIdList?.length; i += 100) {
            const currentGameIds = orderGameIdList?.slice(i, i + 100) || [];
            const itemRes = await getDownloadDataAutoPagination(
              {
                ...params,
                game_ids: currentGameIds || [],
                page_size: 5000,
              },
              downloadFetchApi,
            );
            list = [...list, ...(itemRes?.list || [])];

            if (res === null) {
              res = { ...itemRes };
            }
          }
        } else {
          for (let i = 0; i < orderGameIdList?.length; i += 5000) {
            const currentGameIds = orderGameIdList?.slice(i, i + 5000) || [];
            res = await getDownloadData(
              {
                ...params,
                game_ids: currentGameIds || [],
                page_size: currentGameIds?.length,
              },
              downloadFetchApi,
            );
            list = [...list, ...(res?.list || [])];
            extEntityInfo = { ...extEntityInfo, ...res?.$ext?.entity_info };
          }
        }

        return {
          ...res,
          list,
          orderGameIdList: orderGameIdList || [],
          $ext: { entity_info: extEntityInfo },
        };
      };

      const promises = granularitties.map((granularity) => {
        const fetchParamsNew = { ...fetchParams, granularity };
        return getDownloadDataIntelligence(fetchParamsNew, downloadFetchApi);
      });

      res = await Promise.all(promises);
      if (Array.isArray(res) && res.length > 0) {
        const metrics = appendMetricRatioKey(
          curRef.current?.downloadApiParams?.metrics,
        );

        res = res.map((item) => {
          const itemOrderGameIdList = item?.orderGameIdList || [];
          let itemGameList = item?.list || [];

          if (isGroupBy) {
            sortDownloadGamesDataCommon(itemOrderGameIdList, itemGameList);
          }

          if (downloadKey === 'mobileOverlapTopCharts') {
            itemGameList = groupByCrossAppMetric(itemGameList, item?.$ext);

            sortDownloadGamesMetrics(itemGameList, {
              gameIds: itemOrderGameIdList,
              markets: curRef.current?.params?.download_markets,
            });
          }

          if (downloadKey === 'mobileOverlapDetails') {
            // const result = groupByOverlapDetailsCrossAppMetric(itemGameList, item?.$ext, curRef.current?.params);
            const result = groupByOverlapDetailsCrossAppMetric(
              itemGameList,
              item?.$ext,
            );
            itemGameList = result?.gameMetricsData;

            sortDownloadGamesMetrics(itemGameList, {
              gameIds: result?.gameIds,
              markets: curRef.current?.params?.download_markets,
            });
          }

          const listNew = itemGameList?.map((subItem: any) => {
            const subItemNew = Object.entries(subItem)?.reduce((acc, cur) => {
              const key = cur?.[0] || '';
              let value = cur?.[1];
              // 指标
              if (metrics?.includes(key)) {
                value = Number.parseFloat(value as string);
                if (Number.isNaN(value)) {
                  value = '';
                }
              }
              return { ...acc, [key]: value };
            }, {});
            return { ...subItem, ...subItemNew };
          });
          return { ...item, list: listNew || [] };
        });
      }

    } else if (shouldUseSSE(fetchParams)) {
      if ('marketProfileStatsNew' === downloadKey) {
        try {
          const params = curRef.current?.params || {};
          const { download_metrics: downloadCategories } = fetchParams || {};
          // console.debug('downloadCategoriesdownloadCategoriesfetchParams', fetchParams)
          // console.debug('paramsparams34234234', params)
          // console.debug('downloadCategoriesdownloadCategories', downloadCategories)
          const newFetchParams = {
            ...params,
            ...fetchParams,
          };
          if (isNotEmpty(downloadCategories)) {
            const METRIC_LIST = ['operation', 'games', 'main_genres', 'sub_genres', 'companies'];
            // 过滤下载指标
            const metricList = METRIC_LIST?.filter((item: string) => downloadCategories?.includes(item));
            const allCategoriesPromises = metricList.map((downloadCategory: string) => {
              // 是否是by time
              const isByTime = params?.downlaod_date_method === 'separate';
              const newParams = changeParamsMetricList(newFetchParams, downloadCategory, isByTime);
              // console.debug('newParamsnewParams', newParams)
              return fetchSSEDownload({
                url: downloadFetchApi,
                params: {
                  ...newParams,
                  use_sse_page: {
                    max_size: 100000,
                  },
                },
                onData: (data) => {
                  // 每次收到数据块时触发
                  console.debug('SSE data chunk received:', data.list?.length || 0, 'items');
                },
                onProgress: (progress, current, total) => {
                  // 更新下载进度
                  console.debug(`SSE download progress: ${progress.toFixed(2)}% (${current}/${total})`);
                },
                onError: (error) => {
                  console.error('SSE download error:', error);
                },
              });
            });
            res = await Promise.all(allCategoriesPromises);
            // console.debug('111 allCategoriesRes: resres', res);
          } else {
            throw new Error('market profile stats pc/console/combine 下载接口入参download_metrics字段不能为空');
          }
        } catch (error) {
          console.error('SSE download error:', error);
          setStatus('error');
        }
      } else {
        // SSE 流式下载模式
        res = await fetchSSEDownload({
          url: downloadFetchApi,
          params: fetchParams,
          onData: (data) => {
            // 每次收到数据块时触发
            console.debug('SSE data chunk received:', data.list?.length || 0, 'items');
          },
          onProgress: (progress, current, total) => {
            // 更新下载进度
            console.debug(`SSE download progress: ${progress.toFixed(2)}% (${current}/${total})`);
          },
          onError: (error) => {
            console.error('SSE download error:', error);
            setStatus('error');
          },
        });
      }
    } else {
      res = await getDownloadData(fetchParams, downloadFetchApi);
    }
    // console.debug('resresresres2323', res);
    // console.debug('downloadConfigKey', downloadConfigKey);
    // console.debug('downloadConfig', downloadConfig);
    let sheets;
    try {
      sheets = await downloadConfig?.[downloadConfigKey]?.(res, fetchParams, params);
      // console.debug('sheetssheetssheets', sheets);
    } catch (e) {
      console.error('错误的下载配置', e);
    }
    if (!sheets) {
      return;
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const sheetName in sheets) {
      const sheet = workbook.addWorksheet(
        sheets[sheetName].sheetName || sheetName,
      );
      const newList = sheets[sheetName]?.data || [];
      // console.debug('sheetsheetsheet', sheet);
      // console.debug('newListnewList', newList);
      // 先在这里处理一下样式
      if (sheets[sheetName]?.style == 'normal') {
        setDownloadDataPlus(sheet, newList, defaultCellStyle);
      } else if (sheets[sheetName]?.style == 'summary') {
        setDownloadDataWithSummaryStyle(sheet, newList);
      } else if (sheets[sheetName]?.style == 'first col bold') {
        setDownloadDataWithFirstColBold(sheet, newList);
      } else {
        setDownloadDataWithNoneStyle(sheet, newList);
      }
      defaultRowNumFmt(sheet);
    }

    // console.debug('writeFile_fileName', fileName);
    // console.debug('writeFile_workbook', workbook);
    // 空数据检测
    checkAndReportEmptyData(workbook, {
      downloadKey,
      downloadId: map.get('id') || '',
      params,
      fileName: curRef.current.fileName,
    });
    await writeFile(workbook, fileName);
  };

  /**
   * 发送下载请求并获取数据。
   *
   * @param payload - 要发送的请求数据，类型为 Record<string, any>。
   * @param fetchUrl - 请求的 URL，类型为 string。
   * @returns 返回一个 Promise，解析为请求的响应数据。
   *
   * @example
   * const data = await getDownloadData({ key: 'value' }, '/api/download');
   */
  const getDownloadData: (
    payload: Record<string, any>,
    fetchUrl: string,
  ) => Promise<any> = async (payload, fetchUrl) => {
    const res: any = await http({
      url: fetchUrl,
      method: 'post',
      params: { is_download: true },
      data: payload,
      rawData: true,
    });
    const resData = res?.data;
    if (!!resData) {
      resData.$req = res?.req || {};
      resData.$ext = res?.ext || {};
    }
    return resData;
  };

  /**
   * 将工作簿中的每个工作表导出为 CSV 文件，并将所有 CSV 文件打包为一个 Zip 文件。
   *
   * @param workbook - 要导出的工作簿对象，包含多个工作表。
   * @param fileName - 生成的 Zip 文件的名称。
   *
   * @throws {Error} 如果在生成 Zip 文件或保存文件时发生错误，将设置状态为 'error'。
   *
   * @example
   * const workbook = ...; // 获取工作簿对象
   * writeCSV(workbook, 'exported_data.zip');
   */
  const writeCSV = async (workbook: Workbook, fileName: string) => {
    // 这里生成的csv下载文件，中文是乱码，如何处理
    try {
      const zip = new JSZip();

      // 遍历每个工作表
      workbook.eachSheet((worksheet) => {
        const csvData: string[] = [];
        const percentCols = new Set<number>();
        worksheet.getRow(1).eachCell((cell, colNumber) => {
          if (PER_FMT_LIST?.includes(cell.value)) {
            percentCols.add(colNumber);
          }
        });

        worksheet.eachRow((row, rowNumber) => {
          // @ts-ignore
          // ExcelJS 的 row.values 是 1-indexed，索引0为空，需要使用 slice(1) 跳过
          const rowData = row?.values
            ?.slice(1)
            ?.map((cell: any, index: number) => {
              if (cell) {
                let str = `${cell}`;
                if (rowNumber > 1 && percentCols.has(index + 1)) {
                  const num = Number(cell);
                  if (!Number.isNaN(num)) {
                    str = `${Number((num * 100).toFixed(2))}`;
                  }
                }
                // CSV 本身无类型，Excel 打开时会把 "1-3" / "4-10" 这类
                // rank 区间自动识别为日期（如 2026/1/3、1月3日）。
                // 用公式字符串转义 `="1-3"` 强制 Excel 按字符串显示；
                // Google Sheets / LibreOffice 同样兼容。
                // 排除 YYYY-MM / YYYY-M，避免表格里的年月被包成公式字面量。
                const isRankLikeHyphenPair =
                  /^\d+-\d+$/.test(str) &&
                  !/^\d{4}-\d{1,2}$/.test(str);
                if (isRankLikeHyphenPair) {
                  return `"=""${str}"""`;
                }
                if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                  return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
              }
              return '';
            })
            .join(',');
          csvData.push(rowData);
        });

        // 添加 CSV 文件到 Zip，添加 UTF-8 BOM 解决中文乱码
        zip.file(`${worksheet.name}.csv`, '\uFEFF' + csvData.join('\n'));
      });

      // 生成 Zip 文件并保存
      const content = await zip.generateAsync({ type: 'blob' });
      FileSaver.saveAs(content, `${fileName}.zip`);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  /**
   * 将给定的工作簿写入 XLSX 文件并下载。
   *
   * @param workbook - 要写入的工作簿对象。
   * @param fileName - 下载文件的名称，包括扩展名。
   *
   * @returns {Promise<void>} - 一个 Promise，表示写入和下载操作的完成状态。
   *
   * @throws {Error} - 如果写入或下载过程中发生错误，将会抛出异常。
   */
  const writeXLSX = async (workbook: Workbook, fileName: string) => {
    try {
      const data = await workbook.xlsx.writeBuffer();
      const blob = new Blob([data], { type: EXCEL_TYPE });
      FileSaver.saveAs(blob, `${fileName}.xlsx`);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  /**
   * 将工作簿写入指定的文件格式。
   *
   * @param workbook - 要写入的工作簿对象。
   * @param fileName - 输出文件的名称。
   *
   * @returns {Promise<void>} - 一个表示写入操作完成的 Promise。
   *
   * @throws {Error} - 如果写入操作失败，将抛出错误。
   *
   * @remarks
   * 根据当前参数的文件格式，函数将选择写入 XLSX 或 CSV 格式。
   */
  const writeFile = async (workbook: Workbook, fileName: string) => {
    if (curRef?.current?.params?.file_format === 0) {
      await writeXLSX(workbook, fileName);
    } else {
      await writeCSV(workbook, fileName);
    }
  };
  // 下载参数是否存在
  const ifDownLoadParamsExist = (downLoadParams: string | null) => {
    if (!downLoadParams) {
      JSON.stringify({
        action: 'downloadPageInitFail',
        downloadId: map.get('id'),
        storageParams: downLoadParams,
        storageParams2: sessionStorage.getItem('downLoadParams'),
        storageParams3: sessionStorage.getItem(
          `downLoadParams-${map.get('id')}`,
        ),
      });
      return true;
    }
    return false;
  };
  // 判断是否超过最大数量限制
  const isOverMaxLimit = (
    curLimit = Infinity,
    curTotal = Infinity,
  ): boolean => {
    const isExceeded = curLimit < curTotal;
    if (isExceeded) {
      setStatus('error');
      setMaxErrorVisible(true);
    }
    return isExceeded;
  };
  /**
   * 下载列表类型数据的处理函数。
   *
   * @param sheet - 当前工作表对象，包含工作表的相关信息。
   * @param sheetData - 当前工作表的数据对象，包含要下载的数据。
   *
   * 此函数执行以下操作：
   * 1. 从当前引用中获取工作表的列头，并设置样式。
   * 2. 从工作表数据中提取数据行，并将其添加到下载数据中。
   * 3. 对工作表的默认行号格式进行设置。
   */
  const downloadListTypeData = (sheet: Worksheet, sheetData: SheetData) => {
    // 添加列头并设置样式
    const headers = sheetData?.header?.[0] || [];
    setDownloadHeader(sheet, headers, rowStyle);
    // 添加数据行
    const { data } = sheetData;
    setDownloadData(data, sheet, headers);
    defaultRowNumFmt(sheet);
  };

  /**
   * 设置下载表头。
   *
   * @param sheet - 要添加行的工作表。
   * @param headers - 表头字符串数组。
   * @param rowStyles - 可选的行样式对象，用于设置单元格样式。
   *
   * @returns {void} 无返回值。
   */
  const setDownloadHeader = (
    sheet: Worksheet,
    headers: string[],
    rowStyles?: any,
    isNoHead = false,
  ) => {
    if (isNoHead) {
      return;
    }
    sheet.addRow(headers).eachCell({ includeEmpty: false }, (cell: any) => {
      Object.assign(cell, rowStyles);
    });
  };

  const setDownloadDataPlus = (
    sheet: Worksheet,
    data: string[],
    rowStyles?: any,
  ) => {
    if (data.length === 0) {
      return;
    }
    // 第一行一定是表头
    sheet.addRow(data[0]).eachCell({ includeEmpty: false }, (cell: any) => {
      Object.assign(cell, rowStyle);
    });
    data.slice(1).forEach((e) => {
      sheet.addRow(e).eachCell({ includeEmpty: false }, (cell: any) => {
        Object.assign(cell, rowStyles);
      });
    });
  };

  const setDownloadDataWithNoneStyle = (sheet: Worksheet, data: string[]) => {
    if (data.length === 0) {
      return;
    }
    // 第一行一定是表头
    sheet.addRows(data);
  };

  const setDownloadDataWithSummaryStyle = (
    sheet: Worksheet,
    data: string[],
  ) => {
    if (data.length === 0) {
      return;
    }
    // 第一行一定是表头
    sheet.addRow(data[0]).eachCell({ includeEmpty: false }, (cell: any) => {
      Object.assign(cell, { font: BOLD_FONT });
    });
    if (data.length > 1) {
      sheet.addRows(data.slice(1));
    }
  };

  const setDownloadDataWithFirstColBold = (
    sheet: Worksheet,
    data: string[],
  ) => {
    if (data.length === 0) {
      return;
    }
    // 第一行一定是表头
    for (let i = 0; i < data.length; i++) {
      var j = 0;
      sheet.addRow(data[i]).eachCell({ includeEmpty: false }, (cell: any) => {
        if (j++ == 0) {
          Object.assign(cell, { font: BOLD_FONT });
        }
      });
    }
  };

  /**
   * 根据给定的标题设置下载表头。
   *
   * @param sheet - 要设置表头的工作表。
   * @param headers - 包含标题的对象数组。
   *
   * @remarks
   * 此函数会提取每个标题的 `title` 属性，并将其作为行传递给 `setDownloadHeader` 函数。
   */
  const setDownloadHeaderByTitle = (
    sheet: Worksheet,
    headers: any[],
    isNoHead = false,
  ) => {
    const row = headers?.map((header: any) => header.title);
    setDownloadHeader(sheet, row, rowStyle, isNoHead);
  };
  /**
   * 设置下载数据到指定的工作表。
   *
   * @param data - 包含要下载的数据的对象数组，每个对象代表一行数据。
   * @param sheet - 目标工作表，数据将被写入该工作表。
   * @param headers - 表头数组，用于映射数据对象的属性到工作表的列。
   *
   * @remarks
   * 此函数会遍历每一行数据，并根据提供的表头生成对应的行数据。
   * 如果数据对象的键以 '@_' 开头，则会将其值替换到对应的列中。
   * 最后，调用 `setDownloadHeader` 函数将生成的行数据写入工作表。
   */
  const setDownloadData = (
    data: Record<string, any>[],
    sheet: Worksheet,
    headers: string[],
  ) => {
    data?.forEach((dataRow) => {
      const row = headers?.map((header: string) => dataRow[header] || '');
      Object.keys(dataRow)?.forEach((key) => {
        if (key.startsWith('@_')) {
          const index = parseInt(key.split('_').pop() as string, 10); // 转为零基索引
          row[index] = dataRow[key] || ''; // 替换对应列的值
        }
      });

      setDownloadHeader(sheet, row, defaultCellStyle);
    });
  };
  /**
   * 根据分页数据设置下载数据。
   *
   * @param data - 要处理的数据数组，每个元素是一个记录对象。
   * @param sheet - 当前工作表对象，包含工作表的相关信息。
   * @param headers - 表头数组，包含每个表头的键。
   * @param downloadKey - 下载数据的关键字，用于标识数据类型。
   * @param allData - 所有数据的集合，用于分析和处理。
   *
   * @returns void - 此函数没有返回值，直接修改下载数据。
   *
   * @remarks
   * 如果当前工作表名称长度大于1，则下载关键字将附加工作表名称。
   * 函数会分析数据并为每一行设置下载头部，使用默认单元格样式。
   */
  const setDownloadDataByPagingData = (
    data: Record<string, any>[],
    sheet: Worksheet,
    headers: string[],
    downloadKey: string,
    allData: any,
    params?: any,
  ) => {
    let downloadKeyCurrent = downloadKey;

    const { downloadApiListObj = {} } = curRef.current;

    if (curRef.current?.sheetName?.length > 1 || !isEmpty(downloadApiListObj)) {
      downloadKeyCurrent = `${downloadKey}-${sheet.name}`;
    }

    // 通过配置文件，获取下载数据
    const [newList, tp] = analyseDataByDownloadKey(
      data,
      curRef.current,
      downloadKeyCurrent,
      allData,
      params,
    );
    console.debug('analyseDataByDownloadKey_newList', newList);
    console.debug('analyseDataByDownloadKey_tp', tp);
    // debugger;
    if (tp === 'DIRECT') {
      setDownloadDataPlus(sheet, newList, defaultCellStyle);
    } else {
      let newListCur = [...newList];
      // console.log('newListCur', newListCur);
      // 最多下载10万条数据
      if (Array.isArray(newList) && newList?.length > 0) {
        newListCur = newListCur?.slice(0, 100000);
      }
      newListCur?.forEach((dataRow: any) => {
        const row = headers?.map((header: any) => {
          const headerKey = header?.key;
          const headerFormatter = formatters[header?.formatter];
          const item = dataRow[headerKey];
          const itemFormatted =
            headerFormatter?.(item, dataRow, params) ?? item;
          // console.log('itemitemitemitem', item);
          // if (item === undefined || item === null) return '';
          if (downloadKey === 'pcConsoleMarketCount') {
            return itemFormatted ?? '';
          }
          return isStoreRankMoreWebDownloadKey(downloadKey) &&
            headerKey?.includes('|')
            ? itemFormatted ?? '-'
            : itemFormatted || '';
        });
        console.debug('sheet', sheet);
        console.debug('rowrow', row);
        console.debug('defaultCellStyle', defaultCellStyle);
        setDownloadHeader(sheet, row, defaultCellStyle);
      });
    }
  };

  /**
   * 设置工作表第一行单元格的数字格式。
   * 如果单元格的值为 '%'，则将该列的数字格式设置为 '0.00%'。
   *
   * @param sheet - 要处理的工作表对象。
   */
  const defaultRowNumFmt = (sheet: Worksheet) => {
    sheet.getRow(1).eachCell((cell, colNumber) => {
      const { value } = cell;
      if (PER_FMT_LIST?.includes(value)) {
        // eslint-disable-next-line no-param-reassign
        sheet.getColumn(colNumber).numFmt = '0.00%';
      }
    });
  };
  /**
   * 下载特殊类型数据并将其添加到指定的工作表中。
   *
   * @param sheet - 要将数据添加到的工作表对象。
   * @param paramsData - 包含要下载的数据的对象，数据应为一个数组。
   *
   * 该函数处理以 '@' 和 '_' 开头的特殊指标，并为每一行数据创建一个新行。
   * 对于每个单元格，根据其样式 ID 应用相应的样式。
   *
   * 注意：此函数假定 `styles` 和 `defaultCellStyle` 已在上下文中定义。
   */
  const downloadSpecialTypeData = (sheet: Worksheet, paramsData: SheetData) => {
    // 处理特殊指标（以 @ _ 开头的键）
    const data = paramsData?.data || [];
    data?.forEach((rowData) => {
      // 遍历表格中的每一行数据
      const row = sheet.addRow(
        rowData?.map((cell: { styleId: number; value: string | number }) =>
          cell.styleId == 2
            ? ratioValueFormat(cell.value, 2, false, '')
            : cell.value ?? '',
        ), // 处理空值
      );

      // 为每个单元格应用样式
      row.eachCell((cell, colNumber) => {
        const styleId = rowData[colNumber - 1]?.style_id;
        if (styles[styleId]) {
          Object.assign(cell, defaultCellStyle, styles[styleId]);
        } else {
          Object.assign(cell, defaultCellStyle);
        }
      });

      // 自动调整列
    });
  };
  /**
   * 通过 API 下载数据并生成 Excel 文件。
   *
   * 此函数从当前引用中获取参数、下载 API 和文件名，
   * 然后调用 `getDownloadData` 函数获取工作表数据。
   * 对于每个工作表数据，根据类型调用相应的处理函数，
   * 最后将生成的工作簿写入文件。
   *
   * @async
   * @function downloadByApi
   * @returns {Promise<void>} 返回一个 Promise，表示下载操作的完成状态。
   */
  const downloadByApi = async () => {
    const { params, downloadFetchApi, fileName, downloadKey } = curRef.current;
    const res: SheetData[] = await getDownloadData(params, downloadFetchApi);
    const workbook = new ExcelJS.Workbook();
    // 遍历每个工作表数据
    res?.forEach((sheetData: any) => {
      const sheet = workbook.addWorksheet(sheetData.sheet_name);
      if (sheetData?.type === 'list') {
        downloadListTypeData(sheet, sheetData);
      } else {
        downloadSpecialTypeData(sheet, sheetData);
      }
    });
    // 空数据检测
    checkAndReportEmptyData(workbook, {
      downloadKey,
      downloadId: map.get('id') || '',
      params,
      fileName: curRef.current.fileName,
    });
    await writeFile(workbook, fileName);
  };

  /**
   * 设置是否有下一页的状态。
   *
   * @param res - 服务器返回的响应对象，包含总记录数和是否有更多数据的标志。
   * @param list - 当前页面的数据列表。
   * @param params - 请求参数，包含当前页码和每页大小等信息。
   * @returns 如果有下一页则返回 true，否则返回 false。
   *
   * @remarks
   * - 如果 `fetchOnlyOnce` 为 true，则直接返回 false。
   * - 如果 `params.select_top` 存在且不为 undefined，则根据总记录数判断是否还有下一页。
   * - 否则，根据 `res.has_more` 或当前列表的长度判断是否还有更多数据。
   */
  const setHasNextPage = (res: any, list: any, params: any) => {
    const { page, page_size } = params;
    const pTotal = page * page_size;
    const { fetchOnlyOnce } = curRef.current;
    if (fetchOnlyOnce) return false;
    if (params?.select_top || params?.select_top === 0)
      return pTotal < (res?.total || 0);
    return res?.has_more || list?.length;
  };
  const getTrueHasNextPage = (
    totalValue: any,
    fetchParams: any,
    params: any,
    downloadNum: any,
  ) => {
    const { page, page_size } = fetchParams || {};
    const pTotal = page * page_size;
    const { fetchOnlyOnce } = curRef.current || {};
    if (fetchOnlyOnce) return false;
    if (pTotal >= 100000) return false;
    // downloadNum 已下载多少个
    // totalValue 后端接口查询共有多少个
    // 目标个数smallerMaxNum
    const smallerMaxNum = Math.min(totalValue, params?.select_top);
    // 还剩多少个remaNum
    const remaNum = smallerMaxNum - downloadNum;
    // 本次下载多少个remaNumCur
    const reMaNumCur = remaNum > 500 ? 500 : remaNum;
    return {
      hasNextPage: pTotal < smallerMaxNum,
      reMaNumCur,
    };
  };
  const getHasNextPage = (
    totalValue: any,
    fetchParams: any,
    params: any,
    downloadNum: any,
  ) => {
    const { page, page_size } = fetchParams || {};
    const pTotal = page * page_size;
    const { fetchOnlyOnce } = curRef.current || {};

    // console.log('getHasNextPage-fetchOnlyOnce', fetchOnlyOnce);
    // console.log('getHasNextPage-pTotal', pTotal);

    if (fetchOnlyOnce) return false;
    if (pTotal >= 100000) return false;
    // downloadNum 已下载多少个
    // totalValue 后端接口查询共有多少个
    // 目标个数smallerMaxNum
    const smallerMaxNum = Math.min(totalValue, params?.select_top);
    // 还剩多少个remaNum
    const remaNum = smallerMaxNum - downloadNum;
    // 本次下载多少个remaNumCur
    const reMaNumCur = remaNum > 500 ? 500 : remaNum;

    // console.debug('getHasNextPage-totalValue', totalValue);
    // console.debug('getHasNextPage-fetchParams', fetchParams);
    // console.debug('getHasNextPage-params', params);
    // console.debug('getHasNextPage-downloadNum', downloadNum);
    // console.debug('getHasNextPage-smallerMaxNum', smallerMaxNum);
    // console.debug('getHasNextPage-remaNum', remaNum);
    // console.debug('getHasNextPage-reMaNumCur', reMaNumCur);
    return {
      hasNextPage: pTotal < smallerMaxNum,
      reMaNumCur,
    };
  };
  /**
   * 根据请求的指标列表设置工作表的表头。
   *
   * @param sheet - 要设置表头的工作表对象。
   * @param res - 包含指标列表的响应对象，应该包含一个名为 `metric_list` 的数组。
   *
   * @remarks
   * 此函数会遍历 `metric_list` 中的每个元素，并为每个指标生成一个标题和一个变化标题。
   * 生成的标题将被添加到工作表的第一行，并应用样式。
   * 最后，更新当前引用的 'Store Rank Details'。
   */
  const setSheetHeadByReqMerticList = (sheet: any, res: any) => {
    const { $req, metric_list = [] } = res || {};
    const { regions } = $req || {};
    /**
     * 是否为勾选多国家时的下载
     * 单国家时，展示榜单名
     * 多国家时，展示排名名称（类似1-5、5-20、20-50）
     */
    const isMultiMarkets = Array.isArray(regions) && regions.length > 1;
    const newHeader: any = [];
    metric_list?.forEach((element: any) => {
      // element格式：appstore top free|rank
      const metricKeyIndex = isMultiMarkets ? 1 : 0;
      const metricKey = element ? element.split('|')[metricKeyIndex] : '';
      if (metricKey) {
        newHeader.push(
          {
            title: metricKey,
            key: element,
          },
          {
            title: 'Change',
            key: `${element}_change`,
          },
        );
      }
    });
    newHeader.forEach((element: any, index: number) => {
      sheet.getColumn(7 + index).values = [element.title];
    });
    sheet.getRow(1).eachCell({ includeEmpty: false }, (cell: any) => {
      Object.assign(cell, rowStyle);
    });
    curRef.current['Store Rank Details'] = [
      ...curRef.current['Store Rank Details'],
      ...newHeader,
    ];
  };

  /**
   * 处理静态工作表并将其写入文件。
   *
   * @param sheetNames - 要添加到工作簿中的工作表名称数组。
   *
   * @returns {Promise<void>} - 返回一个 Promise，表示文件写入操作的完成。
   *
   * @throws {Error} - 如果在写入文件或处理工作表时发生错误，将抛出错误。
   *
   * @example
   * // 使用示例
   * const sheetNames = ['Sheet1', 'Sheet2'];
   * await dealStaticSheet(sheetNames);
   */
  const dealStaticSheet = async (sheetNames: string[]) => {
    const { params, fileName, downloadKey } = curRef.current;

    const workbook = new ExcelJS.Workbook();
    sheetNames?.forEach((sheetName) => {
      const sheet = workbook.addWorksheet(sheetName);
      const columns = curRef.current[sheetName];
      setDownloadHeaderByTitle(sheet, columns);
      const { downloadData } = params;
      const headers = columns?.map((item) => item.key);
      setDownloadData(downloadData, sheet, headers);
    });

    // 空数据检测
    checkAndReportEmptyData(workbook, {
      downloadKey,
      downloadId: map.get('id') || '',
      params,
      fileName: curRef.current.fileName,
    });
    await writeFile(workbook, fileName);
  };

  /**
   * 设置摘要工作表。
   *
   * @param workbook - 要添加摘要工作表的工作簿对象。
   *
   * @remarks
   * 此函数从当前引用的状态中提取摘要数据，并将其添加到新的工作表中。
   * 如果摘要数据存在，则会创建一个名为“Summary”的工作表，并将每个摘要项作为行添加到工作表中。
   * 每个单元格将应用默认样式，第一行的单元格将应用加粗字体样式。
   */
  const setSummarySheet = (workbook: any) => {
    const { summarySheet = [] } = curRef.current;
    if (summarySheet?.length) {
      const summary = workbook.addWorksheet('Summary');
      summarySheet.forEach((item: any) => {
        summary.addRow(item).eachCell({ includeEmpty: false }, (cell: any) => {
          Object.assign(cell, defaultCellStyle);
        });
      });
      summary.getRow(1).eachCell({ includeEmpty: false }, (cell: any) => {
        Object.assign(cell, { ...defaultCellStyle, font: BOLD_FONT });
      });
    }
  };
  return (
    <div className="dbo-down-page-wrap">
      <div className="dbo-down-page">
        <div className="dbo-down-box">
          <img src={Downloading} className="dbo-down-box-left" />
          <div className="dbo-down-box-right">
            <div className="dbo-down-box-title">
              {formatMessage({ id: 'Download' })}
            </div>
            <div className="dbo-down-box-desc">
              <div className="dbo-down-box-describe">{desc}</div>
              {/* 最大下载数据条数提示文案 */}
              {isShowMaxDownloadRowsTips === 'true' && (
                <div className="dbi-down-box-text">
                  {formatMessage({
                    id: 'dbi_download_page_maximum_number_of_rows_tips',
                  })}
                </div>
              )}
              <div className="dbo-down-tipbox">
                <div className="dbo-down-tipbox-lf">
                  <img src={XlsxIcon} className="dbo-down-tipbox-img" />
                  <div className="dbo-down-tipbox-filename">
                    {/* 文件名 */}
                    {curFileName}
                  </div>
                </div>
                {/* 下载状态Icon */}
                <Icon className="dbo-down-tipbox-statusicon" type={status} />
              </div>
            </div>

            <div
              id="maximum-tips"
              className={`maximum-tips ${maxErrorVisible ? 'show' : ''}`}
            >
              <p>
                {formatMessage(
                  { id: `Sorry, the export limit is set to {value} rows.` },
                  { value: thousandthFormat(limit) },
                )}
              </p>
              <p>{formatMessage({ id: 'Please re-select the filter' })}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
